/**
 * Import din exportul JSON NECRIPTAT al Bitwarden
 * (`{"encrypted": false, "items": [{type, name, login: {...}, notes, ...}]}`).
 *
 * Tipuri Bitwarden: 1 = login, 2 = notiță securizată, 3 = card, 4 = identitate,
 * 5 = cheie SSH. Login-urile se mapează pe `password` (+ `totp_seed`); restul
 * devin `generic`, cu câmpurile lor scrise ca linii „cheie: valoare" în notițe
 * (criptate). Folderele și colecțiile nu se importă.
 */
import { mapLogin } from '@/features/vault/transfer/mapLogin';
import {
  isRecord,
  text,
  TransferError,
  type ParsedImport,
} from '@/features/vault/transfer/types';

const TYPE_LOGIN = 1;
const TYPE_NOTE = 2;
/** Tipurile fără echivalent în Cerbes și obiectul din care le citim câmpurile. */
const STRUCTURED: Record<number, string> = { 3: 'card', 4: 'identity', 5: 'sshKey' };

/** Liniile „cheie: valoare" pentru valorile nevide ale unui obiect plat. */
function objectLines(value: unknown): string[] {
  if (!isRecord(value)) return [];
  return Object.entries(value)
    .map(([key, v]) => [key, text(v)])
    .filter(([, v]) => v !== '')
    .map(([key, v]) => `${key}: ${v}`);
}

/** Câmpurile personalizate (`fields: [{name, value}]`) ca linii de text. */
function customFieldLines(fields: unknown): string[] {
  if (!Array.isArray(fields)) return [];
  return fields
    .filter(isRecord)
    .map((f) => [text(f.name), text(f.value)])
    .filter(([name, value]) => name !== '' || value !== '')
    .map(([name, value]) => `${name}: ${value}`);
}

function joinBlocks(...blocks: string[]): string {
  return blocks.filter((b) => b !== '').join('\n\n');
}

export function parseBitwarden(raw: string): ParsedImport {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    throw new TransferError('invalid_file');
  }
  if (!isRecord(data)) throw new TransferError('invalid_file');
  if (data.encrypted === true) throw new TransferError('bitwarden_encrypted');
  if (!Array.isArray(data.items)) throw new TransferError('invalid_file');

  const result: ParsedImport = { items: [], skipped: [] };
  for (const item of data.items) {
    if (!isRecord(item)) continue;
    const name = text(item.name);
    const notes = text(item.notes);
    const extra = customFieldLines(item.fields).join('\n');

    if (item.type === TYPE_LOGIN) {
      const login = isRecord(item.login) ? item.login : {};
      const uris = (Array.isArray(login.uris) ? login.uris : [])
        .filter(isRecord)
        .map((u) => text(u.uri))
        .filter((u) => u !== '');
      const mapped = mapLogin({
        title: name,
        url: uris[0] ?? '',
        username: text(login.username),
        password: typeof login.password === 'string' ? login.password : '',
        // URL-urile suplimentare și câmpurile personalizate ajung în notițe.
        notes: joinBlocks(notes, uris.slice(1).join('\n'), extra),
        totp: text(login.totp),
      });
      result.items.push(...mapped.items);
      result.skipped.push(...mapped.skipped);
      continue;
    }

    const structuredKey = typeof item.type === 'number' ? STRUCTURED[item.type] : undefined;
    if (item.type !== TYPE_NOTE && structuredKey === undefined) {
      result.skipped.push({ title: name, code: 'unsupported_type' });
      continue;
    }
    const body = joinBlocks(
      structuredKey ? objectLines(item[structuredKey]).join('\n') : '',
      extra,
      notes,
    );
    if (!name && !body) {
      result.skipped.push({ title: name, code: 'empty_entry' });
      continue;
    }
    result.items.push({
      type: 'generic',
      title: name,
      url: null,
      username: null,
      expires_at: null,
      meta: {},
      payload: body ? { notes: body } : {},
    });
  }
  return result;
}
